import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') === 'signup' ? 'signup' : 'signin';
  const isResetMode = searchParams.get('reset') === 'true';
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [nomeCompleto, setNomeCompleto] = useState("");
  const [nomeEmpresa, setNomeEmpresa] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [googleNomeCompleto, setGoogleNomeCompleto] = useState("");
  const [googleNomeEmpresa, setGoogleNomeEmpresa] = useState("");

  useEffect(() => {
    // Verificar se está vindo do link de reset de senha
    const isResetMode = searchParams.get('reset') === 'true';
    
    const checkAuthAndRedirect = async (session: any) => {
      if (!session || isResetMode) return;

      // Buscar profile com role
      const { data: profile } = await supabase
        .from("profiles")
        .select(`
          tenant_id, 
          nome_completo,
          roles:role_id (slug)
        `)
        .eq("id", session.user.id)
        .single();
      
      // Verificar se é Admin SaaS PRIMEIRO
      const isAdminSaas = profile?.roles?.slug === "admin_saas";
      
      // Se é Admin SaaS, redirecionar diretamente para /clientes-saas (sem pedir dados)
      if (isAdminSaas) {
        navigate("/clientes-saas");
        return;
      }
      
      if (!profile?.tenant_id || !profile?.nome_completo) {
        const isGoogle = !!(session.user?.identities?.some((i: any) => i.provider === "google") || session.user?.app_metadata?.providers?.includes("google"));
        const fullNameMeta = session.user?.user_metadata?.full_name || session.user?.user_metadata?.name || "";
        const emailMeta = session.user?.email || "";
        const defaultCompanyName = fullNameMeta || emailMeta.split("@")[0] || "Minha Empresa";
        const slugBase = defaultCompanyName
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");
        if (isGoogle) {
          try {
            const { error: rpcError } = await supabase
              .rpc("complete_google_signup", {
                p_company_name: defaultCompanyName,
                p_company_slug: slugBase || (emailMeta.split("@")[0] || "minha-empresa"),
                p_full_name: fullNameMeta || emailMeta
              });
            if (rpcError) {
              setGoogleNomeCompleto(fullNameMeta);
              setGoogleNomeEmpresa(defaultCompanyName);
              setShowCompletionModal(true);
              return;
            }
            navigate("/dashboard");
            return;
          } catch (_e) {
            setGoogleNomeCompleto(fullNameMeta);
            setGoogleNomeEmpresa(defaultCompanyName);
            setShowCompletionModal(true);
            return;
          }
        }
        setGoogleNomeCompleto(fullNameMeta);
        setGoogleNomeEmpresa(defaultCompanyName);
        setShowCompletionModal(true);
      } else {
        navigate("/dashboard");
      }
    };
    
    // Verificar sessão atual
    supabase.auth.getSession().then(({ data: { session } }) => {
      checkAuthAndRedirect(session);
    }).catch((error) => {
      console.error("Erro ao obter sessão:", error);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setShowForgotPassword(false);
      } else if (event === 'SIGNED_IN' && session) {
        checkAuthAndRedirect(session);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, searchParams]);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password || !nomeCompleto || !nomeEmpresa) {
      toast.error("Por favor, preencha todos os campos");
      return;
    }

    setLoading(true);
    
    // Gerar slug da empresa
    const slugEmpresa = nomeEmpresa
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Remove acentos
      .replace(/[^a-z0-9]+/g, "-") // Substitui caracteres especiais por hífen
      .replace(/^-+|-+$/g, ""); // Remove hífens do início e fim
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          nome_completo: nomeCompleto,
          company_name: nomeEmpresa,
          company_slug: slugEmpresa,
        },
        emailRedirectTo: `${window.location.origin}/dashboard`,
      },
    });

    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Conta criada! Verifique seu email para confirmar e acessar seu painel.");
      await import("@/services/activities").then(m => m.logAuth("login", "signup"));
    }
    setLoading(false);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error("Por favor, preencha todos os campos");
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      toast.error("Email ou senha incorretos");
    } else {
      await import("@/services/activities").then(m => m.logAuth("login", "password"));
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!resetEmail) {
      toast.error("Por favor, digite seu email");
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: `${window.location.origin}/auth?reset=true`,
    });

    if (error) {
      toast.error("Erro ao enviar email de recuperação");
    } else {
      toast.success("Email de recuperação enviado! Verifique sua caixa de entrada.");
      setShowForgotPassword(false);
      setResetEmail("");
    }
    setLoading(false);
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      toast.error("Por favor, preencha todos os campos");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("A senha deve ter no mínimo 6 caracteres");
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) {
      toast.error("Erro ao atualizar senha");
    } else {
      toast.success("Senha atualizada com sucesso! Redirecionando...");
      setTimeout(() => {
        navigate("/dashboard");
      }, 2000);
    }
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        }
      }
    });

    if (error) {
      toast.error("Erro ao conectar com Google: " + error.message);
      setLoading(false);
    }
  };

  const handleCompleteGoogleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!googleNomeCompleto || !googleNomeEmpresa) {
      toast.error("Por favor, preencha todos os campos");
      return;
    }

    setLoading(true);

    try {
      // Gerar slug da empresa
      const slugEmpresa = googleNomeEmpresa
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

      console.log("Chamando RPC para completar signup:", { 
        companyName: googleNomeEmpresa, 
        slug: slugEmpresa, 
        fullName: googleNomeCompleto 
      });

      // Chamar função RPC segura no banco de dados
      const { data: result, error: rpcError } = await supabase
        .rpc('complete_google_signup', {
          p_company_name: googleNomeEmpresa,
          p_company_slug: slugEmpresa,
          p_full_name: googleNomeCompleto
        });

      console.log("Resultado RPC:", { result, rpcError });

      if (rpcError) {
        console.error("Erro ao chamar RPC:", rpcError);
        throw new Error(`Falha ao completar cadastro: ${rpcError.message}`);
      }

      // Type assertion para o resultado
      const typedResult = result as { success: boolean; error?: string; tenant_id?: string; message?: string };

      if (!typedResult?.success) {
        throw new Error(typedResult?.error || 'Erro desconhecido ao criar empresa');
      }

      toast.success("Cadastro concluído com sucesso! Redirecionando...");
      setShowCompletionModal(false);
      setTimeout(() => {
        navigate("/dashboard");
      }, 1000);
    } catch (error: any) {
      console.error("Erro completo no signup:", error);
      toast.error("Erro ao completar cadastro: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Modal de reset de senha */}
      <Dialog open={isResetMode} onOpenChange={(open) => !open && navigate("/auth")}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Redefinir Senha</DialogTitle>
            <DialogDescription>
              Digite sua nova senha abaixo.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdatePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">Nova Senha</Label>
              <Input
                id="new-password"
                type="password"
                placeholder="Mínimo 6 caracteres"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-cy="new-password"
                autoComplete="new-password"
                required
                minLength={6}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirmar Nova Senha</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder="Digite a senha novamente"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                data-cy="confirm-password"
                autoComplete="new-password"
                required
                minLength={6}
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-cy="update-password-submit">
              {loading ? "Atualizando..." : "Atualizar Senha"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de completar cadastro Google */}
      <Dialog open={showCompletionModal} onOpenChange={setShowCompletionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete seu Cadastro</DialogTitle>
            <DialogDescription>
              Para finalizar, precisamos de algumas informações adicionais.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCompleteGoogleSignup} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="google-empresa">Nome da Empresa</Label>
              <Input
                id="google-empresa"
                type="text"
                placeholder="Nome da sua empresa"
                value={googleNomeEmpresa}
                onChange={(e) => setGoogleNomeEmpresa(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="google-nome">Seu Nome Completo</Label>
              <Input
                id="google-nome"
                type="text"
                placeholder="Seu nome completo"
                value={googleNomeCompleto}
                onChange={(e) => setGoogleNomeCompleto(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Finalizando..." : "Finalizar Cadastro"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 to-accent/10 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-2">
              <img 
                src="/logo-fundo-branco.png" 
                alt="Caixa Fácil Já" 
                className="h-16 w-auto"
              />
            </div>
            <CardDescription>Gerencie seu negócio com facilidade</CardDescription>
          </CardHeader>
        <CardContent>
          <Tabs defaultValue={defaultTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin" data-cy="signin-tab">Entrar</TabsTrigger>
              <TabsTrigger value="signup" data-cy="signup-tab">Cadastrar</TabsTrigger>
            </TabsList>

            <TabsContent value="signin">
              {!showForgotPassword ? (
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <Input
                      id="signin-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-cy="signin-email"
                      autoComplete="email"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="signin-password">Senha</Label>
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-sm text-primary hover:underline"
                        data-cy="forgot-password-link"
                      >
                        Esqueci minha senha
                      </button>
                    </div>
                    <Input
                      id="signin-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-cy="signin-password"
                      autoComplete="current-password"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-cy="signin-submit">
                    {loading ? "Entrando..." : "Entrar"}
                  </Button>
                  
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <Separator />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">
                        Ou continue com
                      </span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleGoogleSignIn}
                    disabled={loading}
                    data-cy="google-signin"
                  >
                    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                    Continuar com Google
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">Email para recuperação</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      data-cy="reset-email"
                      autoComplete="email"
                      required
                    />
                    <p className="text-sm text-muted-foreground">
                      Digite seu email e enviaremos um link para redefinir sua senha.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Button type="submit" className="w-full" disabled={loading} data-cy="reset-submit">
                      {loading ? "Enviando..." : "Enviar Link de Recuperação"}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setResetEmail("");
                      }}
                      data-cy="back-to-login"
                    >
                      Voltar para o login
                    </Button>
                  </div>
                </form>
              )}
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-empresa">Nome da Empresa</Label>
                  <Input
                    id="signup-empresa"
                    type="text"
                    placeholder="Nome da sua empresa"
                    value={nomeEmpresa}
                    onChange={(e) => setNomeEmpresa(e.target.value)}
                    data-cy="signup-empresa"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-nome">Seu Nome Completo</Label>
                  <Input
                    id="signup-nome"
                    type="text"
                    placeholder="Seu nome completo"
                    value={nomeCompleto}
                    onChange={(e) => setNomeCompleto(e.target.value)}
                    data-cy="signup-nome"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-cy="signup-email"
                    autoComplete="email"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Senha</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-cy="signup-password"
                    autoComplete="new-password"
                    required
                    minLength={6}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-cy="signup-submit">
                  {loading ? "Criando conta..." : "Iniciar Teste Grátis - 14 Dias"}
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <Separator />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      Ou cadastre-se com
                    </span>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  data-cy="google-signup"
                >
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Continuar com Google
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
        </Card>
      </div>
    </>
  );
}
